import React, { useState, useCallback } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScanLine, X, Camera, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QRScanner = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [scannedResult, setScannedResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset state when dialog closes - use callback pattern
  const handleOpenChange = useCallback((isOpen) => {
    if (!isOpen) {
      // Reset state when closing
      setError(null);
      setScannedResult(null);
      setIsProcessing(false);
      onClose();
    }
  }, [onClose]);

  const handleScan = (result) => {
    if (result && result.length > 0 && !isProcessing) {
      setIsProcessing(true);
      const scannedValue = result[0].rawValue;
      
      try {
        // Parse the URL to extract the path
        const url = new URL(scannedValue);
        const pathname = url.pathname;
        
        // Check if it's a valid hive or yard URL
        if (pathname.startsWith('/hive/')) {
          const hiveId = pathname.split('/hive/')[1];
          setScannedResult({ type: 'hive', id: hiveId, url: scannedValue });
          setTimeout(() => {
            onClose();
            navigate(`/?view=hive-detail&hiveId=${hiveId}`);
          }, 1000);
        } else if (pathname.startsWith('/yard/')) {
          const yardId = pathname.split('/yard/')[1];
          setScannedResult({ type: 'yard', id: yardId, url: scannedValue });
          setTimeout(() => {
            onClose();
            navigate(`/yard/${yardId}`);
          }, 1000);
        } else if (pathname.startsWith('/swarm-traps')) {
          const trapId = url.searchParams.get('id');
          setScannedResult({ type: 'swarm-trap', id: trapId, url: scannedValue });
          setTimeout(() => {
            onClose();
            navigate(`/swarm-traps?id=${trapId}`);
          }, 1000);
        } else {
          setError('QR code not recognized. Please scan a valid Thrive QR code.');
          setIsProcessing(false);
        }
      } catch (e) {
        // Not a valid URL, might be just a path
        if (scannedValue.startsWith('/hive/') || scannedValue.startsWith('/yard/')) {
          setScannedResult({ type: 'path', url: scannedValue });
          setTimeout(() => {
            onClose();
            navigate(scannedValue);
          }, 1000);
        } else {
          setError('Invalid QR code format. Please scan a Thrive QR code.');
          setIsProcessing(false);
        }
      }
    }
  };

  const handleError = (err) => {
    if (err.name === 'NotAllowedError') {
      setError('Camera access denied. Please allow camera access to scan QR codes.');
    } else if (err.name === 'NotFoundError') {
      setError('No camera found. Please connect a camera to scan QR codes.');
    } else {
      setError('Failed to access camera. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b border-stone-200">
          <DialogTitle className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
            <ScanLine className="h-6 w-6 text-amber-600" />
            Scan QR Code
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          {/* Camera View */}
          {!error && !scannedResult && (
            <div className="relative aspect-square bg-black">
              <Scanner
                onScan={handleScan}
                onError={handleError}
                formats={['qr_code']}
                components={{
                  audio: false,
                  torch: true,
                  finder: true,
                }}
                styles={{
                  container: {
                    width: '100%',
                    height: '100%',
                  },
                  video: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  },
                }}
              />
              
              {/* Scan overlay */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Corner markers */}
                <div className="absolute top-1/4 left-1/4 w-12 h-12 border-l-4 border-t-4 border-amber-500 rounded-tl-lg" />
                <div className="absolute top-1/4 right-1/4 w-12 h-12 border-r-4 border-t-4 border-amber-500 rounded-tr-lg" />
                <div className="absolute bottom-1/4 left-1/4 w-12 h-12 border-l-4 border-b-4 border-amber-500 rounded-bl-lg" />
                <div className="absolute bottom-1/4 right-1/4 w-12 h-12 border-r-4 border-b-4 border-amber-500 rounded-br-lg" />
                
                {/* Scanning line animation */}
                <div className="absolute left-1/4 right-1/4 top-1/4 h-1 bg-amber-500/50 animate-pulse" 
                     style={{ animation: 'scanLine 2s ease-in-out infinite' }} />
              </div>
            </div>
          )}
          
          {/* Error State */}
          {error && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800 mb-2">Camera Error</h3>
              <p className="text-sm text-stone-600 mb-4">{error}</p>
              <Button
                onClick={() => setError(null)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Camera className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}
          
          {/* Success State */}
          {scannedResult && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800 mb-2">
                {scannedResult.type === 'hive' ? '🐝 Hive Found!' : 
                 scannedResult.type === 'yard' ? '🏡 Yard Found!' :
                 scannedResult.type === 'swarm-trap' ? '🪤 Swarm Trap Found!' : 'Navigating...'}
              </h3>
              <p className="text-sm text-stone-500">Redirecting you now...</p>
            </div>
          )}
        </div>
        
        {/* Instructions */}
        {!error && !scannedResult && (
          <div className="p-4 bg-stone-50 border-t border-stone-200">
            <p className="text-xs text-stone-500 text-center">
              Point your camera at a hive or yard QR code to quickly access it
            </p>
          </div>
        )}
        
        {/* Close Button */}
        <div className="p-4 border-t border-stone-200">
          <Button
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            className="w-full h-12"
          >
            <X className="h-4 w-4 mr-2" />
            Close Scanner
          </Button>
        </div>
      </DialogContent>
      
      <style>{`
        @keyframes scanLine {
          0%, 100% { 
            transform: translateY(0);
            opacity: 0.5;
          }
          50% { 
            transform: translateY(200px);
            opacity: 1;
          }
        }
      `}</style>
    </Dialog>
  );
};

export default QRScanner;
